import React, { useMemo, useState } from 'react';
import type {
  AlAdvancedAction,
  AlCategory,
  AlMode,
  AlSimpleDay,
  AlSimpleGroup,
  AlStatus,
  AlTone,
} from '../../growth-autopilot-log/types';
import {
  AL_ADVANCED_ACTIONS,
  AL_SIMPLE_TIMELINE,
  AL_TOTAL_ACTIONS,
} from '../../growth-autopilot-log/mockData';
import {
  AL_CATEGORY_META,
  AL_CATEGORY_ORDER,
  AL_STATUS_META,
  GROWTH_AUTOPILOT_LOG_META,
} from '../../growth-autopilot-log/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-autopilot-log.css';

const TONE: Record<AlTone, { accent: string; tint: string }> = {
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const STATUS_FILTER_ORDER: readonly (AlStatus | 'all')[] = [
  'all',
  'success',
  'retrying',
  'failed',
  'pending',
] as const;

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const MODE_OPTIONS = [
  { value: 'simple', label: 'Simple', icon: 'clock' as const },
  { value: 'advanced', label: 'Advanced', icon: 'list' as const },
];

function AlCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function AlCategoryPill({
  category,
  size = 'md',
}: {
  category: AlCategory;
  size?: 'sm' | 'md';
}) {
  const meta = AL_CATEGORY_META[category];
  const tone = TONE[meta.tone];
  return (
    <span
      className={[
        'inline-flex items-center gap-[5px] whitespace-nowrap rounded-[var(--r-pill)] font-bold',
        size === 'sm' ? 'px-2 py-[2px] text-[10.5px]' : 'px-2.5 py-[3px] text-[11.5px]',
      ].join(' ')}
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.accent }} />
      {meta.label}
    </span>
  );
}

function AlStatusPill({ status }: { status: AlAdvancedAction['status'] }) {
  const meta = AL_STATUS_META[status];
  const tone = TONE[meta.tone];
  return (
    <span
      className="inline-flex items-center gap-[5px] whitespace-nowrap rounded-[var(--r-pill)] px-2.5 py-[3px] text-[11px] font-bold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.accent }} />
      {meta.label}
    </span>
  );
}

function AlActivityGroup({ group }: { group: AlSimpleGroup }) {
  const meta = AL_CATEGORY_META[group.category];
  const tone = TONE[meta.tone];
  const hasDetail = Boolean(group.detail) && group.actionCount > 1;
  const [open, setOpen] = useState(false);

  return (
    <div className="flex gap-4">
      <div className="flex shrink-0 flex-col items-center pt-1">
        <span
          className="h-3 w-3 rounded-full"
          style={{
            background: tone.accent,
            boxShadow: `0 0 0 3px ${tone.tint}`,
          }}
        />
      </div>
      <div className="flex-1 pb-5">
        <div
          className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-[14px] shadow-[var(--sh-1)]"
          style={{ borderLeft: `3px solid ${tone.accent}` }}
        >
          <button
            type="button"
            onClick={() => {
              if (hasDetail) setOpen((value) => !value);
            }}
            className={[
              'flex w-full items-start justify-between gap-3 border-0 bg-transparent p-0 text-left',
              hasDetail ? 'cursor-pointer' : 'cursor-default',
            ].join(' ')}
          >
            <div className="min-w-0">
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <AlCategoryPill category={group.category} size="sm" />
                <span className="text-[11.5px] text-[var(--ink-3)]">{group.time}</span>
                {group.actionCount > 1 ? (
                  <span className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-[7px] py-[1px] font-[var(--font-mono)] text-[10.5px] font-bold text-[var(--ink-2)]">
                    {group.actionCount} actions
                  </span>
                ) : null}
              </div>
              <h3 className="m-0 font-[var(--font-display)] text-[15px] font-semibold leading-[1.3] tracking-[-0.01em] text-[var(--ink-0)]">
                {group.title}
              </h3>
              {group.outcome ? (
                <div className="mt-1.5 flex items-start gap-1.5 text-[12.5px] leading-[1.5] text-[var(--success)]">
                  <Icon
                    kind="check"
                    size={13}
                    stroke={2.5}
                    className="mt-[2px] shrink-0"
                  />
                  <span>{group.outcome}</span>
                </div>
              ) : null}
            </div>
            {hasDetail ? (
              <Icon
                kind="down"
                size={14}
                stroke={2.5}
                className="mt-0.5 shrink-0 text-[var(--ink-4)] transition-transform duration-200"
                style={{ transform: open ? 'rotate(180deg)' : undefined }}
              />
            ) : null}
          </button>
          {open && group.detail ? (
            <div className="elva-fade mt-2.5 border-t border-[var(--cream-2)] pt-2.5 text-[12.5px] leading-[1.6] text-[var(--ink-2)]">
              {group.detail}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AlAdvancedRow({
  action,
  open,
  onToggle,
}: {
  action: AlAdvancedAction;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className={[
          'al-row grid w-full cursor-pointer items-center gap-3.5 border-0 border-b border-[var(--cream-2)] px-4 py-[11px] text-left',
          open ? 'bg-[var(--surface-mute)]' : 'bg-[var(--cream-1)]',
        ].join(' ')}
      >
        <div className="font-[var(--font-mono)] text-[11.5px] text-[var(--ink-2)]">
          {action.timestamp}
        </div>
        <div>
          <AlCategoryPill category={action.category} size="sm" />
        </div>
        <div className="truncate text-[12.5px] font-medium text-[var(--ink-1)]">
          {action.entity}
        </div>
        <div className="truncate text-[12.5px] text-[var(--ink-2)]">{action.detail}</div>
        <div>
          <AlStatusPill status={action.status} />
        </div>
        <div>
          <Icon
            kind="down"
            size={13}
            stroke={2.5}
            className="text-[var(--ink-4)] transition-transform duration-200"
            style={{ transform: open ? 'rotate(180deg)' : undefined }}
          />
        </div>
      </button>
      {open ? (
        <div className="elva-fade border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-4 pl-10">
          <div className="al-detail-grid grid grid-cols-2 gap-5 xl:grid-cols-4">
            <div>
              <div className={`${eyebrowClass} mb-1`}>Action type</div>
              <div className="font-[var(--font-mono)] text-[12.5px] font-medium text-[var(--ink-0)]">
                {action.action}
              </div>
            </div>
            <div>
              <div className={`${eyebrowClass} mb-1`}>Trigger</div>
              <div className="text-[12.5px] text-[var(--ink-0)]">{action.trigger}</div>
            </div>
            <div>
              <div className={`${eyebrowClass} mb-1`}>Before</div>
              <div className="font-[var(--font-mono)] text-[12.5px] text-[var(--ink-2)]">
                {action.before}
              </div>
            </div>
            <div>
              <div className={`${eyebrowClass} mb-1`}>After</div>
              <div className="font-[var(--font-mono)] text-[12.5px] font-medium text-[var(--ink-0)]">
                {action.after}
              </div>
            </div>
          </div>
          <div className="mt-3.5 flex gap-4 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">
            <span>ID: {action.id}</span>
            <span className="text-[var(--ink-4)]">·</span>
            <span>Category: {action.category}</span>
          </div>
        </div>
      ) : null}
    </>
  );
}

function AlSimpleView({
  categoryFilter,
  timeline,
  totalActions,
}: {
  categoryFilter: AlCategory | 'all';
  timeline?: AlSimpleDay[];
  totalActions?: number;
}) {
  const sourceTimeline = timeline ?? AL_SIMPLE_TIMELINE;
  const actionTotal = totalActions ?? AL_TOTAL_ACTIONS;
  const days = useMemo(
    () =>
      sourceTimeline
        .map((day) => ({
          ...day,
          groups:
            categoryFilter === 'all'
              ? day.groups
              : day.groups.filter((group) => group.category === categoryFilter),
        }))
        .filter((day) => day.groups.length > 0),
    [categoryFilter, sourceTimeline],
  );

  return (
    <>
      <AlCard className="mb-5 flex items-center gap-6 border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-6 py-[18px]">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] bg-[var(--cream-1)] text-[var(--success)] shadow-[var(--sh-1)]">
          <Icon kind="bolt" size={24} stroke={2} />
        </span>
        <div className="flex-1">
          <div className={`${eyebrowClass} mb-1 text-[var(--success)]`}>
            ELVA has been working
          </div>
          <div className="font-[var(--font-display)] text-xl font-semibold leading-[1.3] tracking-[-0.02em] text-[var(--ink-0)]">
            <span className="text-[var(--success)]">{actionTotal} actions</span> across
            ads, landing pages, SEO, GBP, and intelligence in the last 7 days
          </div>
        </div>
      </AlCard>

      {days.length === 0 ? (
        <AlCard className="px-6 py-12 text-center text-[14px] text-[var(--ink-3)]">
          No activity in this category for the selected range.
        </AlCard>
      ) : (
        days.map((day, dayIndex) => (
          <div
            key={day.date}
            className={dayIndex === days.length - 1 ? undefined : 'mb-5'}
          >
            <div className="mb-3.5 flex items-baseline gap-3 border-b border-[var(--cream-2)] pb-2.5">
              <h2 className="m-0 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                {day.dayLabel}
              </h2>
              <span className="text-[13px] text-[var(--ink-3)]">{day.date}</span>
              <span className="ml-auto font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
                {day.actionCount} total actions
              </span>
            </div>
            <div>
              {day.groups.map((group) => (
                <AlActivityGroup key={group.id} group={group} />
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );
}

function AlAdvancedView({
  actions,
  activeStatus = 'all',
  onStatusChange,
  onExportCsv,
  exportPending = false,
}: {
  actions?: AlAdvancedAction[];
  activeStatus?: AlStatus | 'all';
  onStatusChange?: (status: AlStatus | 'all') => void;
  onExportCsv?: () => void;
  exportPending?: boolean;
}) {
  const sourceActions = actions ?? AL_ADVANCED_ACTIONS;
  const [openId, setOpenId] = useState<string>(sourceActions[0]?.id ?? '');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sourceActions;
    return sourceActions.filter(
      (action) =>
        action.entity.toLowerCase().includes(query) ||
        action.detail.toLowerCase().includes(query) ||
        action.action.toLowerCase().includes(query),
    );
  }, [search, sourceActions]);

  return (
    <AlCard className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-[var(--cream-2)] px-5 py-4">
        <label className="relative w-[260px]">
          <Icon
            kind="search"
            size={14}
            stroke={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]"
          />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="Search actions, entities, details..."
            className="h-10 w-full rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] pl-9 pr-3 text-[12.5px] text-[var(--ink-1)] outline-none"
          />
        </label>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 text-[12.5px] font-medium text-[var(--ink-2)]"
          >
            <Icon kind="calendar" size={13} stroke={2} className="text-[var(--ink-3)]" />
            Last 7 days
          </button>
          {onStatusChange ? (
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTER_ORDER.map((status) => {
                const active = activeStatus === status;
                const label = status === 'all' ? 'All statuses' : AL_STATUS_META[status].label;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => onStatusChange(status)}
                    className={[
                      'inline-flex h-10 cursor-pointer items-center rounded-[var(--r-md)] border px-3 text-[12.5px] font-medium transition',
                      active
                        ? 'border-[var(--ai-3)] bg-[color-mix(in_srgb,var(--ai-3)_12%,transparent)] text-[var(--ink-0)]'
                        : 'border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)]',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : (
            <button
              type="button"
              className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 text-[12.5px] font-medium text-[var(--ink-2)]"
            >
              <Icon kind="circle" size={13} stroke={2} className="text-[var(--ink-3)]" />
              Status
              <Icon kind="down" size={13} stroke={2.5} className="text-[var(--ink-4)]" />
            </button>
          )}
        </div>
        <button
          type="button"
          disabled={exportPending || !onExportCsv}
          onClick={() => onExportCsv?.()}
          className="ml-auto inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[12.5px] font-medium text-[var(--ink-2)] disabled:cursor-wait disabled:opacity-60"
        >
          <Icon kind="download" size={13} stroke={2} className="text-[var(--ink-3)]" />
          {exportPending ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <div className="al-table-scroll overflow-x-auto">
        <div className="min-w-[920px]">
          <div className="al-row al-head grid items-center gap-3.5 border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-2.5">
            <div className={eyebrowClass}>Timestamp</div>
            <div className={eyebrowClass}>Category</div>
            <div className={eyebrowClass}>Entity</div>
            <div className={eyebrowClass}>Detail</div>
            <div className={eyebrowClass}>Status</div>
            <div />
          </div>
          {rows.map((action) => (
            <AlAdvancedRow
              key={action.id}
              action={action}
              open={openId === action.id}
              onToggle={() => {
                setOpenId((current) => (current === action.id ? '' : action.id));
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-3.5">
        <div className="text-[12px] text-[var(--ink-3)]">
          Showing {rows.length} of {sourceActions.length} actions
          {search ? <span> · Filtered by: &ldquo;{search}&rdquo;</span> : null}
        </div>
      </div>
    </AlCard>
  );
}

export interface AutopilotLogViewProps {
  simpleTimeline?: AlSimpleDay[];
  advancedActions?: AlAdvancedAction[];
  totalActions?: number;
  categoryCounts?: Partial<Record<AlCategory | 'all', number>>;
  activeCategory?: AlCategory | 'all';
  onCategoryChange?: (category: AlCategory | 'all') => void;
  activeStatus?: AlStatus | 'all';
  onStatusChange?: (status: AlStatus | 'all') => void;
  onExportCsv?: () => void;
  exportPending?: boolean;
  loading?: boolean;
}

export function AutopilotLogView({
  simpleTimeline,
  advancedActions,
  totalActions,
  categoryCounts,
  activeCategory: activeCategoryProp,
  onCategoryChange,
  activeStatus = 'all',
  onStatusChange,
  onExportCsv,
  exportPending = false,
  loading = false,
}: AutopilotLogViewProps = {}) {
  const meta = GROWTH_AUTOPILOT_LOG_META;
  const [mode, setMode] = useState<AlMode>('simple');
  const [internalCategory, setInternalCategory] = useState<AlCategory | 'all'>('all');
  const categoryFilter = activeCategoryProp ?? internalCategory;
  const setCategoryFilter = (category: AlCategory | 'all') => {
    onCategoryChange?.(category);
    if (activeCategoryProp === undefined) setInternalCategory(category);
  };

  return (
    <div className="growth-autopilot-log-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="bars" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <Segmented
          options={MODE_OPTIONS}
          value={mode}
          onChange={(value) => {
            setMode(value as AlMode);
          }}
        />
      </div>

      {(mode === 'simple' || onCategoryChange) ? (
        <div className="mb-5 flex flex-wrap gap-1.5">
          {AL_CATEGORY_ORDER.map((cat) => {
            const active = categoryFilter === cat;
            const meta2 = cat === 'all' ? null : AL_CATEGORY_META[cat];
            const tone = meta2 ? TONE[meta2.tone] : null;
            const count = categoryCounts?.[cat];
            return (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  setCategoryFilter(cat);
                }}
                className={[
                  'inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-pill)] border px-3 py-1.5 text-[12.5px] transition',
                  active ? 'font-semibold' : 'font-medium',
                ].join(' ')}
                style={
                  active && tone
                    ? {
                        borderColor: `color-mix(in srgb, ${tone.accent} 45%, transparent)`,
                        background: tone.tint,
                        color: tone.accent,
                      }
                    : active
                      ? {
                          borderColor: 'var(--ink-4)',
                          background: 'var(--surface-mute)',
                          color: 'var(--ink-0)',
                        }
                      : {
                          borderColor: 'var(--cream-2)',
                          background: 'var(--cream-1)',
                          color: 'var(--ink-3)',
                        }
                }
              >
                {tone ? (
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: tone.accent }}
                  />
                ) : null}
                {cat === 'all' ? 'All activity' : meta2?.label}
                {count != null ? (
                  <span className="font-[var(--font-mono)] text-[10.5px] font-bold opacity-80">
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {mode === 'simple' ? (
        <AlSimpleView
          categoryFilter={categoryFilter}
          {...(simpleTimeline ? { timeline: simpleTimeline } : {})}
          {...(totalActions != null ? { totalActions } : {})}
        />
      ) : (
        <AlAdvancedView
          {...(advancedActions ? { actions: advancedActions } : {})}
          activeStatus={activeStatus}
          {...(onStatusChange ? { onStatusChange } : {})}
          {...(onExportCsv ? { onExportCsv } : {})}
          exportPending={exportPending}
        />
      )}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-5 text-[12px] text-[var(--ink-4)]">
        <div>
          {mode === 'simple'
            ? 'Want the full audit trail? Switch to Advanced mode.'
            : 'Need a simpler view? Switch to Simple mode.'}
        </div>
        <div>Log is read-only — actions can be undone from their respective module pages.</div>
      </div>
    </div>
  );
}
